import {
  Megaphone,
  Sparkles,
  Video,
  Radio,
  Rocket,
  Bell,
  Zap,
  Calendar,
  PlayCircle,
  ArrowRight,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { AnnouncementIcon, useAnnouncementBanner } from '@/hooks/useAnnouncementBanner';

const ICON_MAP: Record<AnnouncementIcon, LucideIcon> = {
  Megaphone,
  Sparkles,
  Video,
  Radio,
  Rocket,
  Bell,
  Zap,
  Calendar,
  PlayCircle,
};

interface Props {
  className?: string;
}

export function AnnouncementBanner({ className }: Props) {
  const { data } = useAnnouncementBanner();
  if (!data?.enabled || !data.text) return null;

  const Icon = ICON_MAP[data.icon] ?? Megaphone;
  const hasCta = !!(data.cta_label && data.cta_url);

  const content = (
    <>
      <span className="relative flex h-2 w-2 shrink-0">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
      </span>
      <Icon className="h-4 w-4 text-primary shrink-0" />
      <span className="text-sm font-medium text-foreground truncate">{data.text}</span>
      {hasCta && (
        <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary shrink-0">
          {data.cta_label}
          <ArrowRight className="h-3.5 w-3.5" />
        </span>
      )}
    </>
  );

  const baseClass = cn(
    'inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-1.5 max-w-full',
    'hover:bg-primary/10 hover:border-primary/30 transition-colors',
    className,
  );

  if (hasCta) {
    const isExternal = /^https?:\/\//i.test(data.cta_url);
    return (
      <a
        href={data.cta_url}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        className={baseClass}
      >
        {content}
      </a>
    );
  }
  return <div className={baseClass}>{content}</div>;
}

export default AnnouncementBanner;
